/**
 * 患者聊天相关服务函数
 */

import TencentCloudChat from '@tencentcloud/lite-chat';
import { request } from '@umijs/max';
import { IM_SDK_APP_ID } from '@/config/im.config';
import type {
  CreateAudioMessageOptions,
  CreateCustomMessageOptions,
  CreateFileMessageOptions,
  CreateImageMessageOptions,
  CreateTextMessageOptions,
  GetMembersRequest,
  GetMembersResponse,
  GetMyTasksRequest,
  GetMyTasksResponse,
  GetProjectAlertDetailResponse,
  GetUserSigRequest,
  GetUserSigResponse,
  GetXfyunAuthUrlRequest,
  GetXfyunAuthUrlResponse,
  ImageFileValidationConfig,
  InvokeChatRequest,
  InvokeChatResponse,
  LinkInfo,
  ResolveTaskResponse,
  SpeechToTextOptions,
  XfyunWebSocketMessage,
} from './types';

// ==================== Tasks 缓存机制 ====================

/**
 * Tasks 缓存配置
 */
const TASKS_CACHE_TTL = 2 * 60 * 1000; // 2 分钟

/**
 * Tasks 缓存存储
 * key: `${projectId}:${memberId}` 或 `${projectId}` (项目级别)
 * value: { data, expireAt }
 */
interface TasksCacheItem {
  data: any[];
  expireAt: number;
}
const tasksCache = new Map<string, TasksCacheItem>();

/**
 * 获取 tasks 缓存
 */
function getTasksCache(key: string): any[] | null {
  const item = tasksCache.get(key);
  if (!item) return null;
  if (Date.now() > item.expireAt) {
    tasksCache.delete(key);
    return null;
  }
  return item.data;
}

/**
 * 设置 tasks 缓存
 */
function setTasksCache(key: string, data: any[]): void {
  tasksCache.set(key, {
    data,
    expireAt: Date.now() + TASKS_CACHE_TTL,
  });
}

/**
 * 清除指定患者的 tasks 缓存
 * @param projectId 项目ID
 * @param memberId 患者ID（可选，不传则清除整个项目的缓存）
 */
export function clearTasksCache(projectId?: number, memberId?: string): void {
  if (projectId && memberId) {
    tasksCache.delete(`${projectId}:${memberId}`);
  } else if (projectId) {
    // 清除该项目下所有缓存
    const keysToDelete: string[] = [];
    tasksCache.forEach((_, key) => {
      if (key.startsWith(`${projectId}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => {
      tasksCache.delete(key);
    });
  } else {
    // 清除所有 tasks 缓存
    tasksCache.clear();
  }
}

// ==================== IM SDK 单例 ====================

// 全局复用的 IM SDK 单例，避免重复初始化
let globalChatInstance: any = null;
let globalIMUserId = '';
let globalInitPromise: Promise<{ chat: any; imUserId: string }> | null = null;

// UserSig 过期时间管理
let userSigRefreshTimer: NodeJS.Timeout | null = null; // 自动刷新定时器
const REFRESH_BEFORE_EXPIRE = 5 * 60 * 1000; // 过期前5分钟刷新（毫秒）

type InitIMOptions = {
  requireFresh?: boolean;
  messageApi?: { error: (msg: string) => void };
  silent?: boolean; // 是否静默处理错误（不显示错误提示）
};

/**
 * 检查是否是 UserSig 过期错误
 */
function isUserSigExpiredError(error: any): boolean {
  const code = error?.code;
  const message = (error?.message || error?.data?.message || '').toLowerCase();
  return (
    code === 70001 ||
    message.includes('usersig 已过期') ||
    message.includes('70001') ||
    message.includes('usersig expired')
  );
}

/**
 * 清理并重新初始化 IM 客户端（用于自动刷新）
 */
async function reinitIMClient(): Promise<void> {
  if (globalChatInstance) {
    try {
      await globalChatInstance.logout();
      globalChatInstance.destroy();
    } catch {
      // 忽略销毁错误
    }
    globalChatInstance = null;
    globalIMUserId = '';
  }
  globalInitPromise = null;
  await initIMClient({ requireFresh: true, silent: true });
}

/**
 * 设置 UserSig 过期前5分钟自动刷新
 */
function scheduleUserSigRefresh(expiresIn: number): void {
  if (userSigRefreshTimer) {
    clearTimeout(userSigRefreshTimer);
    userSigRefreshTimer = null;
  }
  const refreshTime = expiresIn * 1000 - REFRESH_BEFORE_EXPIRE;
  if (refreshTime <= 0) {
    console.warn('UserSig 剩余时间不足5分钟，立即刷新');
    reinitIMClient().catch((err) => console.error('自动刷新失败', err));
    return;
  }
  userSigRefreshTimer = setTimeout(() => {
    console.info('UserSig 即将过期，自动刷新中...');
    reinitIMClient().catch((err) => console.error('自动刷新失败', err));
  }, refreshTime);
  console.info('UserSig 自动刷新已安排', {
    expiresInDays: (expiresIn / 86400).toFixed(2),
    refreshInMinutes: (refreshTime / 1000 / 60).toFixed(2),
  });
}

/**
 * 初始化全局 IM SDK 实例（单例）
 * - 已经初始化则直接复用（除非调用方先 destroy 再 init）
 * - 支持 login 失败且为 UserSig 过期时自动用 require_fresh 重试一次
 * - 成功后根据 expires_in 在过期前预约刷新
 */
export async function initIMClient(
  options?: InitIMOptions,
): Promise<{ chat: any; imUserId: string }> {
  if (globalChatInstance && globalIMUserId) {
    return { chat: globalChatInstance, imUserId: globalIMUserId };
  }

  if (globalInitPromise) {
    return globalInitPromise;
  }

  const { requireFresh = false, messageApi, silent = false } = options || {};

  globalInitPromise = (async () => {
    try {
      // 获取 UserSig
      const userSigResponse = await getUserSig({ require_fresh: requireFresh });
      if (userSigResponse.status !== 'success' || !userSigResponse.data) {
        throw new Error('获取 UserSig 失败');
      }

      const { user_id, usersig, expires_in } = userSigResponse.data;

      if (!IM_SDK_APP_ID || IM_SDK_APP_ID === 0) {
        throw new Error(
          'SDKAppID 配置无效，请在 .env 文件中配置 IM_SDK_APP_ID',
        );
      }

      // 创建并登录 SDK 实例
      const chatInstance = TencentCloudChat.create({
        SDKAppID: IM_SDK_APP_ID,
      });
      chatInstance.setLogLevel(0);
      try {
        await chatInstance.login({ userID: user_id, userSig: usersig });
      } catch (loginError: any) {
        // 如果是过期错误，自动重试一次
        if (isUserSigExpiredError(loginError) && !requireFresh) {
          console.info('检测到 UserSig 过期，自动重试...');
          const retryResponse = await getUserSig({ require_fresh: true });
          if (
            retryResponse.status === 'success' &&
            retryResponse.data &&
            retryResponse.data.usersig
          ) {
            await chatInstance.login({
              userID: retryResponse.data.user_id,
              userSig: retryResponse.data.usersig,
            });
            // 使用重试后的数据
            const retryData = retryResponse.data;
            globalChatInstance = chatInstance;
            globalIMUserId = retryData.user_id;
            if (retryData.expires_in > 0) {
              scheduleUserSigRefresh(retryData.expires_in);
            }
            console.info('IM 初始化成功（重试后）', {
              userId: retryData.user_id,
            });
            return { chat: chatInstance, imUserId: retryData.user_id };
          }
        }
        throw loginError;
      }

      globalChatInstance = chatInstance;
      globalIMUserId = user_id;
      // 设置自动刷新
      if (expires_in != null && expires_in > 0) {
        scheduleUserSigRefresh(expires_in);
      }
      console.info('IM 初始化成功', { userId: user_id });
      return { chat: chatInstance, imUserId: user_id };
    } catch (error: any) {
      console.error('IM 初始化失败:', error);
      if (!silent) {
        const isExpired = isUserSigExpiredError(error);
        messageApi?.error?.(
          isExpired
            ? 'IM 登录失败：UserSig 已过期，请稍后重试'
            : `IM 初始化失败: ${error.message || '未知错误'}`,
        );
      }
      globalInitPromise = null;
      throw error;
    }
  })();

  return globalInitPromise;
}

/**
 * 获取已存在的 IM 实例（如果尚未初始化则返回 null）
 */
export function getIMClient(): { chat: any; imUserId: string } | null {
  if (globalChatInstance && globalIMUserId) {
    return { chat: globalChatInstance, imUserId: globalIMUserId };
  }
  return null;
}

/**
 * 销毁全局 IM 实例，登出并清理资源
 */
export async function destroyIMClient() {
  if (!globalChatInstance) return;

  try {
    await globalChatInstance.logout();
  } catch (error) {
    console.error('IM 登出失败:', error);
  }

  try {
    globalChatInstance.destroy();
  } catch (error) {
    console.error('IM destroy 失败:', error);
  }

  globalChatInstance = null;
  globalIMUserId = '';
  globalInitPromise = null;
  // 清除自动刷新定时器
  if (userSigRefreshTimer) {
    clearTimeout(userSigRefreshTimer);
    userSigRefreshTimer = null;
  }
}

/**
 * 根据患者信息生成群组ID
 * 格式：p{project_id}_m{patient_id[-16:]}（去掉连字符）
 * @param patient 患者信息
 * @returns 群组ID
 */
export function generateGroupId(patient: any): string {
  // 获取患者的第一个项目ID（通常患者只有一个活跃项目）
  const projectId = patient.project_participations?.[0]?.project_id;

  if (!projectId || typeof projectId !== 'number') {
    console.warn('患者没有有效的项目ID，无法生成群组ID');
    return '';
  }

  // 使用患者ID（后端user_id），先去掉所有连字符，再取后16个字符
  const patientId = patient.id || patient.user?.id;
  if (!patientId) {
    console.warn('患者没有有效的ID，无法生成群组ID');
    return '';
  }

  // 先去掉所有连字符，再取后16个字符
  const patientIdWithoutHyphens = patientId.replace(/-/g, '');
  const userIdSuffix = patientIdWithoutHyphens.slice(-16);

  // 生成群组ID：p{project_id}_m{user_id[-16:]}（去掉连字符）
  return `p${projectId}_m${userIdSuffix}`;
}

/**
 * 获取患者的最新消息时间
 * @param chat IM SDK 实例
 * @param patient 患者信息
 * @returns 最新消息的时间戳（ISO 字符串），如果没有消息则返回 null
 */
export async function getPatientLatestMessageTime(
  chat: any,
  patient: any,
): Promise<string | null> {
  if (!chat) {
    return null;
  }

  try {
    const groupId = generateGroupId(patient);
    if (!groupId) {
      return null;
    }

    const conversationID = `GROUP${groupId}`;

    // 先尝试直接获取消息列表（如果已经在群组中，可以直接获取）
    try {
      // 分页获取所有历史消息
      let allMessages: any[] = [];
      let nextReqMessageID: string | undefined;

      do {
        const res: any = await chat.getMessageList({
          conversationID,
          nextReqMessageID, // 分页参数
          count: 15, // 每次获取15条
        });

        const messageList = res.data?.messageList || [];
        allMessages = allMessages.concat(messageList);

        // 检查是否还有更多消息
        nextReqMessageID = res.data?.nextReqMessageID;
      } while (nextReqMessageID);

      if (allMessages.length > 0) {
        // 找到时间最新的消息（按 time 字段排序）
        const latestMessage = allMessages.reduce(
          (latest: any, current: any) => {
            const latestTime = latest.time || 0;
            const currentTime = current.time || 0;
            return currentTime > latestTime ? current : latest;
          },
        );
        // 返回时间戳（转换为 ISO 字符串）
        return new Date(latestMessage.time * 1000).toISOString();
      }
      return null;
    } catch (_getMessageError: any) {
      // 如果获取消息失败，可能是因为不在群组中，尝试加入群组
      // 静默处理错误，不输出日志

      // 尝试加入群组
      try {
        await chat.joinGroup({ groupID: groupId });

        // 加入成功后，分页获取所有历史消息
        let allMessages: any[] = [];
        let nextReqMessageID: string | undefined;

        do {
          const res: any = await chat.getMessageList({
            conversationID,
            nextReqMessageID, // 分页参数
            count: 15, // 每次获取15条
          });

          const messageList = res.data?.messageList || [];
          allMessages = allMessages.concat(messageList);

          // 检查是否还有更多消息
          nextReqMessageID = res.data?.nextReqMessageID;
        } while (nextReqMessageID);

        if (allMessages.length > 0) {
          // 找到时间最新的消息（按 time 字段排序）
          const latestMessage = allMessages.reduce(
            (latest: any, current: any) => {
              const latestTime = latest.time || 0;
              const currentTime = current.time || 0;
              return currentTime > latestTime ? current : latest;
            },
          );
          return new Date(latestMessage.time * 1000).toISOString();
        }
        return null;
      } catch (joinError: any) {
        // 静默处理预期的错误：
        // 10013: already group member - 已经在群组中（正常情况）
        // 10015: invalid group id - 群组不存在（正常情况，群组可能尚未创建）
        if (joinError?.code === 10013 || joinError?.code === 10015) {
          return null;
        }
        // 其他未知错误也静默处理
        return null;
      }
    }
  } catch (_error: any) {
    // 静默处理所有错误，避免控制台输出过多错误信息
    return null;
  }
}

/**
 * 批量获取所有患者的最新消息时间
 * @param chat IM SDK 实例
 * @param patients 患者列表
 * @returns 患者ID到最新消息时间的映射
 */
export async function batchGetPatientLatestMessageTimes(
  chat: any,
  patients: any[],
): Promise<Record<string, string>> {
  if (!chat || !patients || patients.length === 0) {
    return {};
  }

  const result: Record<string, string> = {};

  // 并发获取所有患者的最新消息时间（限制并发数，避免过多请求）
  const batchSize = 5; // 每批处理5个患者
  for (let i = 0; i < patients.length; i += batchSize) {
    const batch = patients.slice(i, i + batchSize);
    const promises = batch.map(async (patient) => {
      const timestamp = await getPatientLatestMessageTime(chat, patient);
      if (timestamp) {
        result[patient.id] = timestamp;
      }
    });

    await Promise.allSettled(promises);
  }

  return result;
}

/**
 * 获取当前用户的 UserSig
 * @param params 请求参数
 * @param params.require_fresh 是否强制刷新，默认 false
 */
export async function getUserSig(
  params?: GetUserSigRequest,
  options?: { [key: string]: any },
): Promise<GetUserSigResponse> {
  const { require_fresh = false } = params || {};
  return request('/v1/im/usersig', {
    method: 'GET',
    params: {
      require_fresh,
    },
    ...(options || {}),
  });
}

/**
 * 获取我的会员通讯录（跨项目聚合）
 * @param params 请求参数
 * @param params.query 搜索关键词（手机号），至少需要2个字符
 * @param params.page 页码，默认 1，最小 1
 * @param params.page_size 每页大小，默认 20，最大 2000，最小 1
 * @param params.require_fresh 是否强制刷新，默认 false
 * @param options 其他请求选项
 */
export async function getMembers(
  params?: GetMembersRequest,
  options?: { [key: string]: any },
): Promise<GetMembersResponse> {
  const {
    query = null,
    page = 1,
    page_size = 20,
    require_fresh = false,
  } = params || {};

  return request('/v1/workspace/members', {
    method: 'GET',
    params: {
      ...(query !== null && query !== undefined ? { query } : {}),
      page,
      page_size,
      require_fresh,
    },
    ...(options || {}),
  });
}

/**
 * 获取项目内指定预警的详情
 * @param project_id 项目ID
 * @param alert_id 预警ID（即任务ID，reference_type为"alert"的任务的id）
 * @param options 其他请求选项
 */
export async function getProjectAlertDetail(
  project_id: number,
  alert_id: string,
  options?: { [key: string]: any },
): Promise<GetProjectAlertDetailResponse> {
  if (!project_id || typeof project_id !== 'number') {
    throw new Error('project_id 参数不能为空且必须是数字');
  }

  if (!alert_id || typeof alert_id !== 'string') {
    throw new Error('alert_id 参数不能为空且必须是字符串');
  }

  return request(`/v1/projects/${project_id}/alerts/${alert_id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/**
 * 解决任务（工作台域）
 * 将任务标记为已解决
 * @param task_id 任务ID（UUID）
 * @param resolution_note 解决备注（可选）
 * @param options 其他请求选项
 */
export async function resolveTask(
  task_id: string,
  resolution_note?: string,
  options?: { [key: string]: any },
): Promise<ResolveTaskResponse> {
  if (!task_id || typeof task_id !== 'string') {
    throw new Error('task_id 参数不能为空且必须是字符串');
  }

  const { require_fresh = false, ...restOptions } = options || {};

  return request(`/v1/workspace/tasks/${task_id}/resolve`, {
    method: 'POST',
    params: {
      require_fresh,
    },
    data: {
      resolution_note: resolution_note || '',
    },
    ...restOptions,
  });
}

/**
 * 获取我的待办任务（跨项目聚合）
 * 返回当前员工负责的所有项目中的任务列表
 * @param params 请求参数
 * @param params.task_type 任务类型过滤
 * @param params.priority 优先级过滤
 * @param params.status 状态过滤，默认 pending
 * @param params.page 页码，默认 1
 * @param params.page_size 每页大小，默认 20，最大 100
 * @param params.require_fresh 是否强制刷新，默认 false
 * @param options 其他请求选项
 */
export async function getMyTasks(
  params?: GetMyTasksRequest,
  options?: { [key: string]: any },
): Promise<GetMyTasksResponse> {
  const {
    task_type = null,
    priority = null,
    status = 'pending',
    page = 1,
    page_size = 20,
    require_fresh = false,
  } = params || {};

  return request('/v1/workspace/tasks', {
    method: 'GET',
    params: {
      ...(task_type !== null && task_type !== undefined ? { task_type } : {}),
      ...(priority !== null && priority !== undefined ? { priority } : {}),
      ...(status !== null && status !== undefined ? { status } : {}),
      page,
      page_size,
      require_fresh,
    },
    ...(options || {}),
  });
}

/**
 * 循环分页拉取 /v1/workspace/tasks 的全量任务
 *
 * 与 getMyTasks 的区别：
 * - getMyTasks 只返回单页数据，调用方需要自行处理分页；
 * - getAllMyTasks 会按 pagination.total_pages 循环请求，直到把所有页面的
 *   items 汇总返回，避免因为单页上限（默认 100）导致的数据截断。
 *
 * 返回结构与 GetMyTasksResponse 完全一致，便于作为 getMyTasks 的 drop-in
 * 替换；其中 data.items 为所有页合并后的结果，data.pagination 使用最后一
 * 次请求返回的分页信息（total_items / total_pages 保留以便调用方参考）。
 *
 * @param params 过滤参数（与 getMyTasks 一致，page 参数会被内部覆盖）
 * @param params.page_size 单页大小，默认 100
 * @param options 透传给 request 的其他选项
 */
export async function getAllMyTasks(
  params?: Omit<GetMyTasksRequest, 'page'>,
  options?: { [key: string]: any },
): Promise<GetMyTasksResponse> {
  const { page_size = 100, ...rest } = params || {};

  let currentPage = 1;
  let totalPages = 1;
  const allItems: GetMyTasksResponse['data']['items'] = [];
  let lastResponse: GetMyTasksResponse | null = null;

  do {
    const resp = await getMyTasks(
      { ...rest, page: currentPage, page_size },
      options,
    );
    lastResponse = resp;

    if (resp.status !== 'success') {
      return resp;
    }

    const items = resp.data?.items || [];
    allItems.push(...items);

    const pagination = resp.data?.pagination;
    totalPages = pagination?.total_pages ?? 1;

    if (!pagination || items.length === 0 || currentPage >= totalPages) {
      break;
    }

    currentPage += 1;
  } while (currentPage <= totalPages);

  const lastPagination = lastResponse?.data?.pagination;

  return {
    ...(lastResponse as GetMyTasksResponse),
    data: {
      items: allItems,
      pagination: {
        total_items: lastPagination?.total_items ?? allItems.length,
        total_pages: lastPagination?.total_pages ?? 1,
        page: 1,
        page_size: lastPagination?.page_size ?? page_size,
      },
    },
  };
}

/**
 * 获取指定患者的所有pending状态任务
 * 使用新的 /v1/workspace/tasks API（跨项目聚合）
 * @param project_id 项目ID（用于缓存key，可选，如果提供则同时过滤项目ID）
 * @param member_id 患者ID（member_id）
 * @param options 其他请求选项（支持 require_fresh 强制刷新缓存）
 */
export async function getPatientPendingTasks(
  project_id: number | null,
  member_id: string,
  options?: { [key: string]: any; require_fresh?: boolean },
): Promise<
  Array<{
    id: string;
    task_type: string;
    project_id: number;
    member_id: string;
    reference_type: string;
    reference_id: string;
    title: string;
    description: string | null;
    priority: string;
    due_date: string | null;
    status: string;
    resolved_by_user_id: string | null;
    resolved_at: string | null;
    resolution_note: string | null;
    created_at: string;
    updated_at: string;
    is_overdue: boolean;
  }>
> {
  if (!member_id || typeof member_id !== 'string') {
    throw new Error('member_id 参数不能为空且必须是字符串');
  }

  const { require_fresh = false, ...restOptions } = options || {};

  // 尝试从缓存获取（如果不强制刷新）
  // 如果提供了 project_id，使用 project_id:member_id 作为缓存key
  // 否则只使用 member_id 作为缓存key
  const cacheKey = project_id
    ? `${project_id}:${member_id}`
    : `all:${member_id}`;
  if (!require_fresh) {
    const cached = getTasksCache(cacheKey);
    if (cached) {
      return cached;
    }
  }

  let allTasks: Array<{
    id: string;
    task_type: string;
    project_id: number;
    member_id: string;
    reference_type: string;
    reference_id: string;
    title: string;
    description: string | null;
    priority: string;
    due_date: string | null;
    status: string;
    resolved_by_user_id: string | null;
    resolved_at: string | null;
    resolution_note: string | null;
    created_at: string;
    updated_at: string;
    is_overdue: boolean;
  }> = [];

  // 使用新的 /v1/workspace/tasks API 循环分页获取所有任务（单页上限 1000）
  const tasksResponse = await getAllMyTasks(
    {
      status: 'pending',
      require_fresh,
    },
    restOptions,
  );

  if (tasksResponse.status !== 'success') {
    throw new Error(tasksResponse.message || '获取任务列表失败');
  }

  allTasks = allTasks.concat(tasksResponse.data.items);

  // 筛选出与指定患者相关的pending任务
  // 如果提供了 project_id，同时过滤项目ID；否则只按 member_id 过滤
  const patientPendingTasks = allTasks.filter((task) => {
    if (task.member_id !== member_id || task.status !== 'pending') {
      return false;
    }
    // 如果提供了 project_id，需要匹配项目ID；否则不过滤项目ID
    if (project_id !== null && project_id !== undefined) {
      return task.project_id === project_id;
    }
    return true;
  });

  // 缓存结果
  setTasksCache(cacheKey, patientPendingTasks);

  return patientPendingTasks;
}

// 需要用户手动处理的重要任务类型（不应该自动 resolve）
// 包括：一级/二级/三级预警、依从性风险、高风险/中风险/低风险任务
const MANUAL_RESOLVE_TASK_TYPES = [
  'alert_level_1',
  'alert_level_2',
  'alert_level_3',
  'compliance_risk_task',
  'high_risk',
  'medium_risk',
  'low_risk',
  'follow_up',
];

/**
 * 判断任务是否需要手动处理（不应该自动 resolve）
 * @param task 任务对象
 * @returns 如果需要手动处理返回 true，否则返回 false
 */
function isManualResolveTask(task: {
  task_type: string;
  reference_type?: string;
}): boolean {
  return MANUAL_RESOLVE_TASK_TYPES.includes(task.task_type);
}

/**
 * 批量解决指定患者的所有pending任务（排除重要预警任务）
 * 注意：一级/二级/三级预警和依从性风险任务需要用户手动处理，不会被自动 resolve
 * @param project_id 项目ID
 * @param member_id 患者ID（member_id）
 * @param resolution_note 解决备注（可选）
 * @param options 其他请求选项
 */
export async function resolvePatientPendingTasks(
  project_ids: number[],
  member_id: string,
  resolution_note?: string,
  options?: { [key: string]: any },
): Promise<{
  success: boolean;
  resolvedCount: number;
  failedCount: number;
  skippedCount: number; // 跳过的重要任务数量
  errors?: Array<{ taskId: string; error: string }>;
}> {
  try {
    // 强制刷新缓存，确保获取最新的任务数据
    const { require_fresh = true, ...restOptions } = options || {};

    // 如果没有项目ID，直接返回
    if (!project_ids || project_ids.length === 0) {
      return {
        success: true,
        resolvedCount: 0,
        failedCount: 0,
        skippedCount: 0,
      };
    }

    // 获取该患者的所有pending任务（不按项目过滤）
    const allPendingTasks = await getPatientPendingTasks(
      null, // 不按项目过滤，获取该患者在所有项目中的任务
      member_id,
      { ...restOptions, require_fresh },
    );

    // 过滤出属于指定项目列表的任务
    const pendingTasks = allPendingTasks.filter((task) =>
      project_ids.includes(task.project_id),
    );

    if (pendingTasks.length === 0) {
      return {
        success: true,
        resolvedCount: 0,
        failedCount: 0,
        skippedCount: 0,
      };
    }

    // 过滤出可以自动 resolve 的任务（排除重要预警任务和所有alert类型的任务）
    const autoResolvableTasks = pendingTasks.filter(
      (task) => !isManualResolveTask(task),
    );

    // 计算跳过的重要任务数量
    const skippedCount = pendingTasks.length - autoResolvableTasks.length;

    if (autoResolvableTasks.length === 0) {
      return {
        success: true,
        resolvedCount: 0,
        failedCount: 0,
        skippedCount,
      };
    }

    // 批量resolve可自动处理的任务
    const resolveResults = await Promise.allSettled(
      autoResolvableTasks.map((task) =>
        resolveTask(
          task.id,
          resolution_note || '退出聊天页面时自动处理',
          restOptions,
        ),
      ),
    );

    // 统计成功和失败的数量
    let resolvedCount = 0;
    let failedCount = 0;
    const errors: Array<{ taskId: string; error: string }> = [];

    resolveResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        resolvedCount += 1;
      } else {
        failedCount += 1;
        const errorMsg = result.reason?.message || '未知错误';
        errors.push({
          taskId: autoResolvableTasks[index].id,
          error: errorMsg,
        });
      }
    });

    // 清除该患者的 tasks 缓存（因为任务状态已改变）
    // 清除所有相关项目的缓存
    if (resolvedCount > 0) {
      project_ids.forEach((projectId) => {
        clearTasksCache(projectId, member_id);
      });
      // 同时清除 all:member_id 的缓存
      tasksCache.delete(`all:${member_id}`);
    }

    return {
      success: failedCount === 0,
      resolvedCount,
      failedCount,
      skippedCount,
      errors: errors.length > 0 ? errors : undefined,
    };
  } catch (error: any) {
    console.error('批量解决pending任务失败:', error);
    return {
      success: false,
      resolvedCount: 0,
      failedCount: 0,
      skippedCount: 0,
      errors: [{ taskId: 'unknown', error: error.message || '未知错误' }],
    };
  }
}

/**
 * 创建文本消息实例
 * @param chat 腾讯云 IM SDK 实例
 * @param options 创建消息的参数
 * @returns 消息实例（Message 对象），可用于发送消息
 */
export function createTextMessage(
  chat: any,
  options: CreateTextMessageOptions,
): any {
  if (!chat) {
    throw new Error('chat 实例不能为空');
  }

  if (!options.to) {
    throw new Error('to 参数不能为空');
  }

  if (!options.conversationType) {
    throw new Error('conversationType 参数不能为空');
  }

  if (!options.text) {
    throw new Error('text 参数不能为空');
  }

  const {
    to,
    conversationType,
    text,
    priority = TencentCloudChat.TYPES.MSG_PRIORITY_NORMAL,
    cloudCustomData = '',
    receiverList,
    isSupportExtension = false,
    needReadReceipt = false,
  } = options;

  // 构建消息参数
  const messageOptions: any = {
    to,
    conversationType,
    priority,
    payload: {
      text,
    },
    isSupportExtension,
    needReadReceipt,
  };

  // 只有当 cloudCustomData 有值时才添加（SDK 不允许空字符串）
  if (cloudCustomData && cloudCustomData.trim() !== '') {
    messageOptions.cloudCustomData = cloudCustomData;
  }

  // 如果有 receiverList，添加到参数中
  if (receiverList && receiverList.length > 0) {
    messageOptions.receiverList = receiverList;
  }

  // 调用 SDK 的 createTextMessage 方法创建消息实例
  const message = chat.createTextMessage(messageOptions);

  return message;
}

/**
 * 验证图片文件
 * @param file 图片文件对象
 * @param config 验证配置
 * @returns 验证结果，如果通过返回 null，否则返回错误信息
 */
export function validateImageFile(
  file: File,
  config?: ImageFileValidationConfig,
): string | null {
  const {
    maxSize = 10 * 1024 * 1024, // 默认 10MB
    allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
    ],
  } = config || {};

  // 验证文件类型
  if (!allowedTypes.includes(file.type)) {
    return '不支持的图片格式，请选择 jpg、jpeg、gif、png、bmp 或 webp 格式的图片';
  }

  // 验证文件大小
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return `图片大小不能超过 ${maxSizeMB}MB`;
  }

  return null;
}

/**
 * 创建图片消息实例
 * @param chat 腾讯云 IM SDK 实例
 * @param options 创建消息的参数
 * @returns 消息实例（Message 对象），可用于发送消息
 */
export function createImageMessage(
  chat: any,
  options: CreateImageMessageOptions,
): any {
  if (!chat) {
    throw new Error('chat 实例不能为空');
  }

  if (!options.to) {
    throw new Error('to 参数不能为空');
  }

  if (!options.conversationType) {
    throw new Error('conversationType 参数不能为空');
  }

  if (!options.file) {
    throw new Error('file 参数不能为空');
  }

  // 如果是 File 对象，进行验证
  if (options.file instanceof File) {
    const validationError = validateImageFile(options.file);
    if (validationError) {
      throw new Error(validationError);
    }
  }

  const {
    to,
    conversationType,
    file,
    priority = TencentCloudChat.TYPES.MSG_PRIORITY_NORMAL,
    cloudCustomData = '',
    customModerationConfigurationID,
    onProgress,
    receiverList,
    isSupportExtension = false,
    needReadReceipt = false,
  } = options;

  // 构建消息参数
  const messageOptions: any = {
    to,
    conversationType,
    priority,
    payload: {
      file,
    },
    isSupportExtension,
    needReadReceipt,
  };

  // 只有当 cloudCustomData 有值时才添加（SDK 不允许空字符串）
  if (cloudCustomData && cloudCustomData.trim() !== '') {
    messageOptions.cloudCustomData = cloudCustomData;
  }

  // 如果有 onProgress，添加到参数中
  if (onProgress) {
    messageOptions.onProgress = onProgress;
  }

  // 如果有 customModerationConfigurationID，添加到参数中
  if (customModerationConfigurationID) {
    messageOptions.customModerationConfigurationID =
      customModerationConfigurationID;
  }

  // 如果有 receiverList，添加到参数中
  if (receiverList && receiverList.length > 0) {
    messageOptions.receiverList = receiverList;
  }

  // 调用 SDK 的 createImageMessage 方法创建消息实例
  const message = chat.createImageMessage(messageOptions);

  return message;
}

/**
 * 从文本中提取URL
 * @param text 文本内容
 * @returns 提取到的URL数组
 */
export function extractUrls(text: string): string[] {
  // URL正则表达式，匹配 http://、https://、www. 开头的URL
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}[^\s]*)/gi;
  const matches = text.match(urlRegex);
  return matches ? [...new Set(matches)] : []; // 去重
}

/**
 * 验证URL格式
 * @param url URL字符串
 * @returns 是否为有效的URL
 */
export function isValidUrl(url: string): boolean {
  try {
    // 如果URL没有协议，添加https://
    const urlWithProtocol =
      url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * 解析链接信息（简化版，实际可以使用Open Graph API获取更详细信息）
 * @param url URL字符串
 * @returns 链接信息
 */
export function parseLinkInfo(url: string): LinkInfo {
  try {
    // 确保URL有协议
    const urlWithProtocol =
      url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);

    return {
      url: urlWithProtocol,
      domain: urlObj.hostname,
      title: urlObj.hostname,
      description: urlWithProtocol,
    };
  } catch {
    return {
      url:
        url.startsWith('http://') || url.startsWith('https://')
          ? url
          : `https://${url}`,
      title: url,
      description: url,
    };
  }
}

/**
 * 创建自定义消息实例
 * @param chat 腾讯云 IM SDK 实例
 * @param options 创建消息的参数
 * @returns 消息实例（Message 对象），可用于发送消息
 */
export function createCustomMessage(
  chat: any,
  options: CreateCustomMessageOptions,
): any {
  if (!chat) {
    throw new Error('chat 实例不能为空');
  }

  if (!options.to) {
    throw new Error('to 参数不能为空');
  }

  if (!options.conversationType) {
    throw new Error('conversationType 参数不能为空');
  }

  if (!options.data) {
    throw new Error('data 参数不能为空');
  }

  const {
    to,
    conversationType,
    data,
    description = '',
    extension = '',
    priority = TencentCloudChat.TYPES.MSG_PRIORITY_NORMAL,
    cloudCustomData = '',
  } = options;

  // 构建消息参数
  const messageOptions: any = {
    to,
    conversationType,
    priority,
    payload: {
      data,
      description,
      extension,
    },
    isSupportExtension: false,
    needReadReceipt: false,
  };

  // 只有当 cloudCustomData 有值时才添加（SDK 不允许空字符串）
  if (cloudCustomData && cloudCustomData.trim() !== '') {
    messageOptions.cloudCustomData = cloudCustomData;
  }

  // 调用 SDK 的 createCustomMessage 方法创建消息实例
  const message = chat.createCustomMessage(messageOptions);

  return message;
}

/**
 * 创建文件消息实例
 * @param chat 腾讯云 IM SDK 实例
 * @param options 创建消息的参数
 * @returns 消息实例（Message 对象），可用于发送消息
 */
export function createFileMessage(
  chat: any,
  options: CreateFileMessageOptions,
): any {
  if (!chat) {
    throw new Error('chat 实例不能为空');
  }

  if (!options.to) {
    throw new Error('to 参数不能为空');
  }

  if (!options.conversationType) {
    throw new Error('conversationType 参数不能为空');
  }

  if (!options.file) {
    throw new Error('file 参数不能为空');
  }

  const {
    to,
    conversationType,
    file,
    priority = TencentCloudChat.TYPES.MSG_PRIORITY_NORMAL,
    cloudCustomData = '',
    onProgress,
    receiverList,
    isSupportExtension = false,
    needReadReceipt = false,
  } = options;

  // 构建消息参数
  const messageOptions: any = {
    to,
    conversationType,
    priority,
    payload: {
      file,
    },
    isSupportExtension,
    needReadReceipt,
  };

  // 只有当 cloudCustomData 有值时才添加（SDK 不允许空字符串）
  if (cloudCustomData && cloudCustomData.trim() !== '') {
    messageOptions.cloudCustomData = cloudCustomData;
  }

  // 如果有 onProgress，添加到参数中
  if (onProgress) {
    messageOptions.onProgress = onProgress;
  }

  // 如果有 receiverList，添加到参数中
  if (receiverList && receiverList.length > 0) {
    messageOptions.receiverList = receiverList;
  }

  // 调用 SDK 的 createFileMessage 方法创建消息实例
  const message = chat.createFileMessage(messageOptions);

  return message;
}

/**
 * 创建音频消息
 * 用于发送语音消息
 * @param chat IM SDK 实例
 * @param options 创建音频消息参数
 * @returns 音频消息实例
 */
export function createAudioMessage(
  chat: any,
  options: CreateAudioMessageOptions,
): any {
  if (!chat) {
    throw new Error('chat 实例不能为空');
  }

  if (!options.to) {
    throw new Error('to 参数不能为空');
  }

  if (!options.conversationType) {
    throw new Error('conversationType 参数不能为空');
  }

  if (!options.file) {
    throw new Error('file 参数不能为空');
  }

  const {
    to,
    conversationType,
    file,
    priority = TencentCloudChat.TYPES.MSG_PRIORITY_NORMAL,
    cloudCustomData = '',
    receiverList,
    isSupportExtension = false,
    needReadReceipt = false,
  } = options;

  // 构建消息参数
  const messageOptions: any = {
    to,
    conversationType,
    priority,
    payload: {
      file,
    },
    isSupportExtension,
    needReadReceipt,
  };

  // 只有当 cloudCustomData 有值时才添加（SDK 不允许空字符串）
  if (cloudCustomData && cloudCustomData.trim() !== '') {
    messageOptions.cloudCustomData = cloudCustomData;
  }

  // 如果有 receiverList，添加到参数中
  if (receiverList && receiverList.length > 0) {
    messageOptions.receiverList = receiverList;
  }

  // 调用 SDK 的 createAudioMessage 方法创建消息实例
  const message = chat.createAudioMessage(messageOptions);

  return message;
}

/**
 * 调用 AI 生成回复接口
 * @param params 请求参数
 * @param params.message 用户消息（必需）
 * @param params.history 对话历史（可选）
 * @param params.prompts Prompt 版本覆盖字典（可选）
 * @param params.require_fresh 是否强制刷新（可选，查询参数）
 * @param options 其他请求选项
 */
export async function invokeChat(
  params: InvokeChatRequest,
  options?: { [key: string]: any },
): Promise<InvokeChatResponse> {
  if (!params.message || typeof params.message !== 'string') {
    throw new Error('message 参数不能为空且必须是字符串');
  }

  const { require_fresh = false, ...restParams } = params;

  return request('/v1/chat/invoke', {
    method: 'POST',
    params: {
      require_fresh,
    },
    data: {
      message: restParams.message,
      ...(restParams.history && restParams.history.length > 0
        ? { history: restParams.history }
        : {}),
      ...(restParams.prompts && Object.keys(restParams.prompts).length > 0
        ? { prompts: restParams.prompts }
        : {}),
    },
    ...(options || {}),
  });
}

// ==================== 讯飞语音听写（语音转文字） ====================

/**
 * 获取讯飞语音听写鉴权 URL
 * 前端使用返回的 ws_url 直连讯飞 WebSocket
 */
export async function getXfyunAuthUrl(
  params?: GetXfyunAuthUrlRequest,
  options?: { [key: string]: any },
): Promise<GetXfyunAuthUrlResponse> {
  const { require_fresh = false, language, domain, accent } = params || {};
  const res = await request<GetXfyunAuthUrlResponse>(
    '/v1/speech/xfyun/auth-url',
    {
      method: 'POST',
      params: { require_fresh },
      data: {
        ...(language != null ? { language } : {}),
        ...(domain != null ? { domain } : {}),
        ...(accent != null ? { accent } : {}),
      },
      ...(options || {}),
    },
  );
  if (res.status !== 'success' || !res.data) {
    throw new Error(res.message || '获取讯飞鉴权 URL 失败');
  }
  return res;
}

/**
 * 从讯飞 WebSocket 返回的 result 中提取文本（按 ws[].cw[].w 拼接）
 */
export function extractTextFromResult(data: {
  result?: {
    sn?: number;
    ws?: Array<{ cw?: Array<{ w: string }> }>;
  };
}): { sn: number; text: string } {
  const result = data?.result;
  const sn = result?.sn ?? 0;
  let text = '';
  const ws = result?.ws ?? [];
  for (const wObj of ws) {
    const cw = wObj?.cw ?? [];
    for (const c of cw) {
      if (c?.w) text += c.w;
    }
  }
  return { sn, text };
}

/** Int16Array 指定范围转 Base64（用于讯飞帧 body） */
function pcmSliceToBase64(
  audioData: Int16Array,
  offset: number,
  length: number,
): string {
  const slice = audioData.subarray(offset, offset + length);
  const bytes = new Uint8Array(
    slice.buffer,
    slice.byteOffset,
    slice.byteLength,
  );
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

const XFYUN_SAMPLE_RATE = 16000;
const XFYUN_FRAME_SIZE_BYTES = 1280;
const XFYUN_FRAME_SIZE_SAMPLES = 640;
const XFYUN_FRAME_INTERVAL_MS = 40;

/**
 * 使用讯飞语音听写 WebSocket 将 PCM 转为文字
 * @param audioData 16k、16bit、单声道 PCM（Int16Array）
 * @param options 可选参数及 onProgress / onError 回调
 */
export function speechToText(
  audioData: Int16Array,
  options?: SpeechToTextOptions,
): Promise<string> {
  const {
    language = 'zh_cn',
    domain = 'iat',
    accent = 'mandarin',
    onProgress,
    onError,
  } = options ?? {};

  const resultsBySn = new Map<number, string>();
  const joinAllResults = (): string => {
    const sns = Array.from(resultsBySn.keys()).sort((a, b) => a - b);
    return sns.map((sn) => resultsBySn.get(sn) ?? '').join('');
  };

  return getXfyunAuthUrl({ language, domain, accent })
    .then(
      (authRes) =>
        new Promise<string>((resolve, reject) => {
          const { ws_url, app_id } = authRes.data;
          let resolved = false;
          const handleSuccess = (text: string) => {
            if (resolved) return;
            resolved = true;
            resolve(text);
          };
          const handleErr = (err: string) => {
            if (resolved) return;
            resolved = true;
            onError?.(err);
            reject(new Error(err));
          };

          const ws = new WebSocket(ws_url);
          let sendOffset = 0;
          let timeoutId: ReturnType<typeof setTimeout> | null = null;

          const frameCount = Math.ceil(
            audioData.length / XFYUN_FRAME_SIZE_SAMPLES,
          );
          const durationSec = audioData.length / XFYUN_SAMPLE_RATE;
          const extraWait = Math.min(durationSec * 2000, 30000) + 5000;
          const totalTimeout = frameCount * XFYUN_FRAME_INTERVAL_MS + extraWait;

          timeoutId = setTimeout(() => {
            if (resolved) return;
            const text = joinAllResults();
            if (text.trim()) {
              handleSuccess(text);
            } else {
              handleErr('识别超时');
            }
          }, totalTimeout);

          const sendNextFrame = () => {
            if (!ws || ws.readyState !== WebSocket.OPEN || resolved) return;
            if (sendOffset >= audioData.length) return;

            const len = Math.min(
              XFYUN_FRAME_SIZE_SAMPLES,
              audioData.length - sendOffset,
            );
            const isLast = sendOffset + len >= audioData.length;
            const status = isLast ? 2 : 1;
            const audioB64 = pcmSliceToBase64(audioData, sendOffset, len);
            sendOffset += len;

            const payload: Record<string, unknown> = {
              data: {
                status,
                format: 'audio/L16;rate=16000',
                encoding: 'raw',
                audio: audioB64,
              },
            };
            ws.send(JSON.stringify(payload));

            if (!isLast) {
              setTimeout(sendNextFrame, XFYUN_FRAME_INTERVAL_MS);
            }
          };

          ws.onopen = () => {
            const len = Math.min(XFYUN_FRAME_SIZE_SAMPLES, audioData.length);
            const audioB64 = pcmSliceToBase64(audioData, 0, len);
            const firstFrame = {
              common: { app_id },
              business: { language, domain, accent },
              data: {
                status: 0,
                format: 'audio/L16;rate=16000',
                encoding: 'raw',
                audio: audioB64,
              },
            };
            ws.send(JSON.stringify(firstFrame));
            sendOffset = len;
            if (sendOffset < audioData.length) {
              setTimeout(sendNextFrame, XFYUN_FRAME_INTERVAL_MS);
            }
          };

          ws.onmessage = (event: MessageEvent) => {
            try {
              const result = JSON.parse(
                event.data as string,
              ) as XfyunWebSocketMessage;
              if (result.code != null && result.code !== 0) {
                handleErr(`讯飞错误: ${result.code}`);
                ws.close();
                return;
              }
              const d = result.data;
              if (d?.result) {
                const { sn, text } = extractTextFromResult(d);
                resultsBySn.set(sn, text);
                onProgress?.(joinAllResults());
              }
              if (d?.status === 2) {
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = null;
                handleSuccess(joinAllResults());
                ws.close();
              }
            } catch (e) {
              handleErr('解析讯飞返回失败');
              ws.close();
            }
          };

          ws.onerror = () => {
            handleErr('WebSocket 错误');
          };

          ws.onclose = () => {
            if (resolved) return;
            if (timeoutId) clearTimeout(timeoutId);
            const text = joinAllResults();
            if (text.trim()) {
              handleSuccess(text);
            } else {
              handleErr('未识别到文本');
            }
          };
        }),
    )
    .catch((err: any) => {
      const msg = err?.message ?? '获取鉴权或建连失败';
      onError?.(msg);
      throw new Error(msg);
    });
}
